import { useState, useRef } from "react";
import { Upload, X, FileText, AlertCircle } from "lucide-react";
import { toast } from "@/lib/toast";

/**
 * Reusable drag-and-drop file picker.
 * Props:
 *  - onFilesAdded(files: File[])
 *  - accept: string (e.g. ".pdf,.jpg,.png") optional
 *  - maxSize: number (bytes) default 25MB
 *  - multiple: boolean default true
 *  - files: File[] current external list (optional, for preview+remove)
 *  - onRemove(idx)
 *  - label, sublabel, compact, disabled
 *  - testIdPrefix
 */
export default function DropZone({
  onFilesAdded,
  accept,
  maxSize = 25 * 1024 * 1024,
  multiple = true,
  files = [],
  onRemove,
  label = "Glissez-deposez vos fichiers ici",
  sublabel = "ou cliquez pour selectionner",
  compact = false,
  disabled = false,
  testIdPrefix = "dropzone",
}) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const extAllowed = accept
    ? accept.split(",").map(e => e.trim().toLowerCase())
    : null;

  const validateFiles = (raw) => {
    const ok = [];
    const rejected = [];
    for (const f of raw) {
      if (f.size > maxSize) {
        rejected.push(`${f.name}: trop volumineux (${(f.size / 1024 / 1024).toFixed(1)} MB)`);
        continue;
      }
      if (extAllowed) {
        const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
        if (!extAllowed.includes(ext)) {
          rejected.push(`${f.name}: type non autorise (${ext})`);
          continue;
        }
      }
      // Dedupe against existing list by name+size
      const dup = files.some(existing => existing.name === f.name && existing.size === f.size);
      if (dup) {
        rejected.push(`${f.name}: deja ajoute`);
        continue;
      }
      ok.push(f);
    }
    if (rejected.length > 0) {
      const msg = rejected.slice(0, 3).join(" | ");
      setError(msg);
      toast.error(msg, { duration: 4000 });
    } else {
      setError("");
    }
    return ok;
  };

  const handleFiles = (fileList) => {
    if (disabled) return;
    const raw = Array.from(fileList || []);
    if (raw.length === 0) return;
    const ok = validateFiles(raw);
    if (ok.length > 0) onFilesAdded(multiple ? ok : [ok[0]]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const formatSize = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="w-full" data-testid={`${testIdPrefix}-container`}>
      <div
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`
          flex flex-col items-center justify-center cursor-pointer
          border-2 border-dashed rounded-lg transition-all
          ${compact ? "px-3 py-3" : "px-4 py-6"}
          ${dragActive
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"}
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        `}
        data-testid={`${testIdPrefix}-zone`}
      >
        <Upload className={`${compact ? "w-4 h-4" : "w-6 h-6"} text-gray-400 ${dragActive ? "text-blue-500" : ""}`} />
        <p className={`${compact ? "text-xs" : "text-sm"} font-medium text-gray-700 mt-1.5`}>{label}</p>
        {!compact && <p className="text-xs text-gray-400 mt-0.5">{sublabel}</p>}
        {accept && !compact && (
          <p className="text-[10px] text-gray-400 mt-1">
            Types: {accept} — Max {formatSize(maxSize)}
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          disabled={disabled}
          data-testid={`${testIdPrefix}-input`}
        />
      </div>

      {error && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-red-600" data-testid={`${testIdPrefix}-error`}>
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-2 space-y-1" data-testid={`${testIdPrefix}-files`}>
          {files.map((f, i) => (
            <div
              key={`${f.name}-${f.size}-${i}`}
              className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 text-xs border"
              data-testid={`${testIdPrefix}-file-${i}`}
            >
              <FileText className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <span className="flex-1 truncate font-medium text-gray-700">{f.name}</span>
              <span className="text-gray-400 flex-shrink-0">{formatSize(f.size)}</span>
              {onRemove && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                  className="text-red-400 hover:text-red-600 flex-shrink-0"
                  data-testid={`${testIdPrefix}-remove-${i}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
